import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Apply custom formatting on initial load
    this.applyCustomPriceFormatting();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Custom price formatting function - removes .00 and currency codes
   */
  applyCustomPriceFormatting() {
    const priceElements = this.querySelectorAll('.price, .money, [class*="price"]');
    
    priceElements.forEach(priceElement => {
      this.formatSinglePriceElement(priceElement);
    });
  }

  /**
   * Format a single price element
   */
  formatSinglePriceElement(priceElement) {
    if (!priceElement) return;
    
    const priceText = priceElement.textContent || priceElement.innerHTML;
    const priceMatch = priceText.match(/[\d,]+\.?\d*/);
    
    if (priceMatch) {
      const numericPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      const formattedPrice = `$${Math.floor(numericPrice)}`;
      priceElement.textContent = formattedPrice;
      priceElement.innerHTML = formattedPrice;
    }
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    
    if (!newPrice || !currentPrice) return;
    
    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      // Format the new price BEFORE displaying it
      this.formatPriceElement(newPrice);
      
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * Format a single price element
   */
  formatPriceElement(container) {
    const priceElements = container.querySelectorAll('.price, .money, [class*="price"]');
    
    priceElements.forEach(priceElement => {
      if (!priceElement) return;
      
      const priceText = priceElement.textContent || priceElement.innerHTML;
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      
      if (priceMatch) {
        const numericPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
        const formattedPrice = `$${Math.floor(numericPrice)}`;
        priceElement.textContent = formattedPrice;
        priceElement.innerHTML = formattedPrice;
      }
    });
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}